import { PAPEIS, ROTULO_PAPEL } from '@baiocchi/shared'
import {
  Botao,
  Cartao,
  Entrada,
  EstadoVazio,
  Etiqueta,
  RodapePaginacao,
  Selecao,
  TabelaDados,
  cn,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import type { UsuarioNaLista } from '@baiocchi/shared'
import { usuariosApi } from '../../api/organizacao'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeBusca, IconeEditar, IconeMais } from '../../componentes/icones'
import { useDebounce } from '../../ganchos/usar-debounce'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { opcoesDeReferencia, useEquipes, useUnidades } from '../../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarDataHora, formatarTempoRelativo } from '../../utilitarios/formatar'

/**
 * TELA-09 — Lista de usuários.
 *
 * **Não existe excluir.** Usuário é desativado, nunca apagado, para preservar o
 * histórico de atendimentos (RN-06/RN-53): a coluna de ações traz apenas editar, e o
 * `DELETE /usuarios/{uuid}` não existe na API.
 *
 * ⚠️ O módulo `/usuarios` ainda não foi publicado pela API. A tela mostra o aviso de
 * recurso indisponível com o endpoint que falta, em vez de tela branca.
 */
export function PaginaListaUsuarios() {
  const { filtros, definir, limpar, alternarOrdenacao, ordenacao } = useFiltrosUrl()
  const navegar = useNavigate()
  const { pode } = useSessao()

  const [pagina, setPagina] = useState(1)
  const [limite, setLimite] = useState(50)
  const [buscaLocal, setBuscaLocal] = useState(filtros.busca ?? '')
  const buscaAtrasada = useDebounce(buscaLocal)

  const unidades = useUnidades()
  const equipes = useEquipes(filtros.unidadeId)

  // Só `buscaAtrasada`: o efeito escreve em `filtros.busca` e reagir à própria
  // escrita o faria disparar duas vezes por letra digitada.
  useEffect(() => {
    if ((filtros.busca ?? '') !== buscaAtrasada) {
      definir({ busca: buscaAtrasada || undefined })
      setPagina(1)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaAtrasada])

  const consultaFiltros = {
    ...filtros,
    limite,
    pagina,
    ordenar: filtros.ordenar ?? 'nome',
  }

  const consulta = useQuery({
    queryKey: chaves.usuarios.lista(consultaFiltros),
    queryFn: ({ signal }) => usuariosApi.listar(consultaFiltros, signal),
    retry: false,
    staleTime: 30_000,
  })

  const colunas: ReadonlyArray<ColunaTabela<UsuarioNaLista>> = [
    {
      chave: 'nome',
      cabecalho: 'Nome',
      ordenavel: true,
      celula: (usuario) => (
        <div className="flex min-w-0 flex-col">
          <Link
            to={`/usuarios/${usuario.uuid}`}
            onClick={(evento) => evento.stopPropagation()}
            className="truncate font-medium text-marca-600 hover:underline"
          >
            {usuario.nome}
          </Link>
          <span className="truncate text-xs text-conteudo-fraco">{usuario.email}</span>
        </div>
      ),
    },
    {
      chave: 'papel',
      cabecalho: 'Nível',
      celula: (usuario) => <Etiqueta tom="neutro">{ROTULO_PAPEL[usuario.papel]}</Etiqueta>,
    },
    {
      chave: 'equipes',
      cabecalho: 'Equipes',
      ocultarAte: 'md',
      celula: (usuario) =>
        usuario.equipes.length === 0 ? (
          <Etiqueta tom="atencao">Sem equipe</Etiqueta>
        ) : (
          <span className="flex flex-wrap gap-1">
            {usuario.equipes.slice(0, 2).map((equipe) => (
              <Etiqueta key={equipe.id} tom="neutro">
                {equipe.titulo}
              </Etiqueta>
            ))}
            {usuario.equipes.length > 2 ? (
              <Etiqueta tom="neutro" title={usuario.equipes.map((e) => e.titulo).join(', ')}>
                +{usuario.equipes.length - 2}
              </Etiqueta>
            ) : null}
          </span>
        ),
    },
    {
      chave: 'unidade',
      cabecalho: 'Unidade',
      ocultarAte: 'lg',
      celula: (usuario) => (
        <span className="text-xs">{usuario.unidadePrincipal?.titulo ?? '—'}</span>
      ),
    },
    {
      chave: 'ultimoAcessoEm',
      cabecalho: 'Último acesso',
      ordenavel: true,
      ocultarAte: 'lg',
      celula: (usuario) => (
        <span
          className="text-xs text-conteudo-suave"
          title={formatarDataHora(usuario.ultimoAcessoEm)}
        >
          {usuario.ultimoAcessoEm ? formatarTempoRelativo(usuario.ultimoAcessoEm) : 'nunca'}
        </span>
      ),
    },
    {
      chave: 'receberLeads',
      cabecalho: 'Recebe leads',
      alinhamento: 'centro',
      ocultarAte: 'md',
      celula: (usuario) => (
        <Etiqueta tom={usuario.receberLeads ? 'sucesso' : 'neutro'}>
          {usuario.receberLeads ? 'Sim' : 'Não'}
        </Etiqueta>
      ),
    },
    {
      chave: 'ativo',
      cabecalho: 'Status',
      alinhamento: 'centro',
      celula: (usuario) => (
        <Etiqueta tom={usuario.ativo ? 'sucesso' : 'perigo'}>
          {usuario.ativo ? 'Ativo' : 'Desativado'}
        </Etiqueta>
      ),
    },
    {
      chave: 'acoes',
      cabecalho: '',
      alinhamento: 'direita',
      celula: (usuario) => (
        <Botao
          variante="fantasma"
          tamanho="icone"
          aria-label={`Editar ${usuario.nome}`}
          onClick={(evento) => {
            evento.stopPropagation()
            // `void`: manipulador de clique, e react-router 7 devolve `Promise`.
            void navegar(`/usuarios/${usuario.uuid}`)
          }}
        >
          <IconeEditar className="h-4 w-4" />
        </Botao>
      ),
    },
  ]

  const itens = consulta.data?.dados ?? []

  return (
    <>
      <CabecalhoPagina
        titulo="Usuários"
        descricao="Usuário nunca é excluído — apenas desativado, para preservar o histórico."
        acoes={
          pode('USUARIO_CRIAR') ? (
            <Botao
              onClick={() => navegar('/usuarios/novo')}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Novo usuário
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Entrada
            value={buscaLocal}
            onChange={(evento) => setBuscaLocal(evento.target.value)}
            placeholder="Buscar por nome ou e-mail…"
            prefixo={<IconeBusca className="h-4 w-4" />}
            aria-label="Buscar usuários"
          />

          <Selecao
            vazio="Todos os níveis"
            aria-label="Filtrar por nível"
            value={filtros.papel ?? ''}
            opcoes={PAPEIS.map((papel) => ({ valor: papel, rotulo: ROTULO_PAPEL[papel] }))}
            onChange={(evento) => {
              definir({ papel: evento.target.value || undefined })
              setPagina(1)
            }}
          />

          <Selecao
            vazio="Todas as unidades"
            aria-label="Filtrar por unidade"
            value={filtros.unidadeId ?? ''}
            opcoes={opcoesDeReferencia(unidades.data?.dados)}
            onChange={(evento) => {
              definir({ unidadeId: evento.target.value || undefined, equipeId: undefined })
              setPagina(1)
            }}
          />

          <Selecao
            vazio="Todas as equipes"
            aria-label="Filtrar por equipe"
            value={filtros.equipeId ?? ''}
            opcoes={opcoesDeReferencia(equipes.data?.dados)}
            onChange={(evento) => {
              definir({ equipeId: evento.target.value || undefined })
              setPagina(1)
            }}
          />

          <Selecao
            vazio="Ativos e desativados"
            aria-label="Filtrar por status"
            value={filtros.ativo ?? ''}
            opcoes={[
              { valor: 'true', rotulo: 'Somente ativos' },
              { valor: 'false', rotulo: 'Somente desativados' },
            ]}
            onChange={(evento) => {
              definir({ ativo: evento.target.value || undefined })
              setPagina(1)
            }}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={filtros.semEquipe === 'true'}
            onClick={() => {
              definir({ semEquipe: filtros.semEquipe === 'true' ? undefined : 'true' })
              setPagina(1)
            }}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
              filtros.semEquipe === 'true'
                ? 'border-atencao bg-atencao text-white'
                : 'border-borda-forte bg-superficie text-conteudo-suave hover:bg-superficie-2',
            )}
            title="Atendente sem equipe não recebe lead na distribuição nem cria lead — é um cadastro incompleto."
          >
            Sem equipe (cadastro incompleto)
          </button>

          {Object.keys(filtros).length > 0 && (
            <Botao
              variante="fantasma"
              tamanho="sm"
              onClick={() => {
                limpar()
                setBuscaLocal('')
                setPagina(1)
              }}
            >
              Limpar filtros
            </Botao>
          )}
        </div>
      </Cartao>

      <Cartao semPadding>
        <TabelaDados
          colunas={colunas}
          itens={itens}
          chaveDe={(usuario) => usuario.uuid}
          ordenacao={ordenacao}
          onOrdenar={(campo) => {
            alternarOrdenacao(campo)
            setPagina(1)
          }}
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro
                erro={consulta.error}
                aoTentarNovamente={() => void consulta.refetch()}
              />
            ) : undefined
          }
          onClicarLinha={(usuario) => navegar(`/usuarios/${usuario.uuid}`)}
          realce={(usuario) => !usuario.ativo}
          vazio={<EstadoVazio titulo="Nenhum usuário encontrado" />}
          cartaoMobile={(usuario) => (
            <Link
              to={`/usuarios/${usuario.uuid}`}
              className="flex flex-col gap-1.5 rounded-lg border border-borda bg-superficie p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-conteudo">{usuario.nome}</p>
                  <p className="truncate text-xs text-conteudo-fraco">{usuario.email}</p>
                </div>
                <Etiqueta tom={usuario.ativo ? 'sucesso' : 'perigo'}>
                  {usuario.ativo ? 'Ativo' : 'Desativado'}
                </Etiqueta>
              </div>
              <div className="flex flex-wrap gap-1">
                <Etiqueta tom="neutro">{ROTULO_PAPEL[usuario.papel]}</Etiqueta>
                {usuario.receberLeads ? <Etiqueta tom="sucesso">Recebe leads</Etiqueta> : null}
              </div>
            </Link>
          )}
        />

        <RodapePaginacao
          limite={limite}
          onAlterarLimite={(novo) => {
            setLimite(novo)
            setPagina(1)
          }}
          quantidadeNaPagina={itens.length}
          indiceInicial={itens.length === 0 ? 0 : (pagina - 1) * limite + 1}
          total={consulta.data?.paginacao.total ?? null}
          pagina={pagina}
          temAnterior={pagina > 1}
          temProximo={itens.length === limite}
          onAnterior={() => setPagina((valor) => Math.max(1, valor - 1))}
          onProximo={() => setPagina((valor) => valor + 1)}
        />
      </Cartao>
    </>
  )
}
